import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Markdown } from '@summer-health/shared/content/react';
import { AnalyticsButton } from '@summer-health/ui/care-analytics';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { ContentfulInterstitialPage } from './contentful-interstitial-page.types';

export type InterstitialCardProps = {
  content: ContentfulInterstitialPage;
  navigateUrlOverride?: string;
  variables?: Map<string, string>;
};

const LanguageSwitcher = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const newLocale = i18n.language === 'en' ? 'es' : 'en';
  const router = useRouter();

  return (
    <Button
      key="language-switcher"
      fullWidth
      color="quaternary"
      onClick={() => {
        const { pathname, asPath, query } = router;
        router
          .push({ pathname, query }, asPath, {
            locale: newLocale,
          })
          .then(() => {
            router.reload();
          });
      }}
    >
      {t('menu.switchLanguage')}
    </Button>
  );
};

export const InterstitialCard = ({
  content,
  navigateUrlOverride,
  variables,
}: InterstitialCardProps): JSX.Element => {
  if (!content) {
    return <></>;
  }
  const { name, header, body, buttonsCollection, buttons } = content;
  const buttonItems = buttons ?? buttonsCollection?.items ?? [];
  return (
    <Card variant="standard">
      <CardHeader
        title={
          <Typography variant="h2" component="h1" paddingBottom={0}>
            {header?.replace(
              '%child_names%',
              variables?.get('child_names') || '',
            )}
          </Typography>
        }
      />
      <CardContent>
        <Markdown content={body} />
        {buttonItems.length ? (
          <Divider
            variant="standard"
            sx={(theme): object => ({
              marginTop: theme.spacing(2),
            })}
          />
        ) : null}
      </CardContent>
      {buttonItems.length ? (
        <CardActions>
          <Stack spacing={1.5} width="100%">
            {buttonItems.map((button) => {
              const search =
                typeof window !== 'undefined' ? window.location.search : '';
              const buttonUrl =
                (button.url ?? '').replace(
                  '%care_number%',
                  variables?.get('care_number') || '',
                ) + search;
              const isLoginUrl =
                buttonUrl.includes('/auth/login') ||
                buttonUrl.includes('/app-login') ||
                buttonUrl.includes('/account');
              return (
                <AnalyticsButton
                  key={button.displayText}
                  fullWidth
                  color={button.color}
                  cardId={`interstitial:${name}`}
                  pageSection={`interstitial:${name}`}
                  to={
                    navigateUrlOverride && !isLoginUrl
                      ? navigateUrlOverride
                      : buttonUrl
                  }
                >
                  {button.displayText}
                </AnalyticsButton>
              );
            })}
            <LanguageSwitcher />
          </Stack>
        </CardActions>
      ) : null}
    </Card>
  );
};
